import { FunctionComponent, PropsWithChildren } from "react";
import { Card, alpha, styled, useTheme } from "@mui/material";
import { useShadows } from "../../builder/Theme";

export type PanelVariant = "plain" | "elegant" | "sleek";

export type ExtendedPanelVariant = PanelVariant | "swift";

export type PanelComponentVariant =
  | ExtendedPanelVariant
  | "swift-left"
  | "swift-right";

interface Props {
  variant?: PanelComponentVariant;
  className?: string;
  backgroundColor?: string;
  textColor?: string;
}

export function usePanelPadding(variant: PanelComponentVariant) {
  const defaultSize = "24px";
  const swiftDefaultPadding = `${defaultSize} 0`;
  let paddingMobile = "0";
  let paddingDesktop = paddingMobile;
  switch (variant) {
    case "elegant":
    case "sleek":
      paddingMobile = defaultSize;
      paddingDesktop = defaultSize;
      break;
    case "swift":
      paddingMobile = swiftDefaultPadding;
      paddingDesktop = swiftDefaultPadding;
      break;
    case "swift-left":
      paddingMobile = swiftDefaultPadding;
      paddingDesktop = `${defaultSize} ${defaultSize} ${defaultSize} 0`;
      break;
    case "swift-right":
      paddingMobile = swiftDefaultPadding;
      paddingDesktop = `${defaultSize} 0 ${defaultSize} ${defaultSize}`;
      break;
    default:
      break;
  }
  return {
    panelPaddingMobile: `${paddingMobile} !important`,
    panelPaddingDesktop: `${paddingDesktop} !important`,
  };
}

export function usePanelColor(variant: PanelComponentVariant, color?: string) {
  const theme = useTheme();
  return variant !== "plain" && variant !== "sleek"
    ? color || theme.palette.text.panelsDefault
    : theme.palette.text.default;
}

function usePanelBackgroundColor(
  variant: PanelComponentVariant,
  color?: string,
) {
  const theme = useTheme();
  return variant !== "plain" && variant !== "sleek"
    ? color || theme.palette.panelsBackground.default
    : "transparent";
}

const StyledCard = styled(Card)<{
  customVariant: PanelComponentVariant;
  backgroundColor?: string;
  textColor?: string;
}>(({ theme, customVariant, backgroundColor, textColor }) => {
  const { panels } = theme.settings;
  const swiftVariant =
    customVariant === "swift" ||
    customVariant === "swift-left" ||
    customVariant === "swift-right";
  const boxShadow = useShadows(panels.shadows);
  const { panelPaddingMobile: padding } = usePanelPadding(customVariant);
  const color = usePanelColor(customVariant, textColor);
  const background = usePanelBackgroundColor(customVariant, backgroundColor);
  const border =
    customVariant === "elegant" || customVariant === "sleek"
      ? {
          borderColor: alpha(
            theme.palette.inputsUnderline.main,
            panels.border.opacity,
          ),
          borderStyle: panels.border.opacity > 0 ? "solid" : "none",
          borderWidth: `${panels.border.thickness}px`,
          borderRadius: panels.border.radius * 5,
        }
      : {
          borderRadius: 0,
        };

  return {
    display: "flex",
    flex: 1,
    padding,
    ...border,
    boxShadow: customVariant === "sleek" ? boxShadow : "none",
    color,
    "& .MuiTypography-root": {
      color: `${color}!important`,
    },
    position: "relative",
    zIndex: 0,
    overflow: "visible",
    ...(swiftVariant
      ? {
          "::after": {
            content: "''",
            position: "absolute",
            zIndex: -1,
            width: 50000,
            height: "100%",
            left: -25000,
            top: 0,
            background,
          },
        }
      : {}),
    [theme.breakpoints.up("md")]: {
      ...(swiftVariant
        ? {
            "::after": {
              left: customVariant === "swift-right" ? 0 : -25000,
              right: customVariant === "swift-left" ? 0 : "initial",
            },
          }
        : {}),
    },
    backgroundColor: background,
  };
});

const Panel: FunctionComponent<PropsWithChildren<Props>> = ({
  children,
  className,
  variant = "plain",
  backgroundColor,
  textColor,
}) => (
  <StyledCard
    customVariant={variant}
    elevation={0}
    className={className}
    backgroundColor={backgroundColor}
    textColor={textColor}
  >
    {children}
  </StyledCard>
);

export default Panel;
